import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { Benchmark } from './benchmark.model';
import { BenchmarkStore } from './benchmark.store';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { Observable } from 'rxjs';
import { BenchmarkQuery } from './benchmark.query';
import { Cacheable } from 'ts-cacheable';
import { addDays } from 'date-fns';

@Injectable({ providedIn: 'root' })
export class BenchmarkService {
    constructor(
        private benchmarkStore: BenchmarkStore,
        private config: AppConfigQuery,
        private http: HttpClient
    ) {}

    getAvailable(): Observable<Array<Benchmark>> {
        return this.http
            .get<Array<Benchmark>>(`${this.config.get().gatewayBaseUrl}/index`)
            .pipe(
                tap((benchmarks) => {
                    this.benchmarkStore.upsertMany(benchmarks);
                })
            );
    }

    @Cacheable({ maxAge: 1000 * 60 * 60 * 12, maxCacheCount: 10 })
    get(id: string) {
        return this.http
            .get<Benchmark>(`${this.config.get().gatewayBaseUrl}/index/benchmarks`, {
                params: {
                    comparison: id
                }
            })
            .pipe(
                tap((benchmark) => {
                    this.benchmarkStore.upsert(id, benchmark);
                })
            );
    }

    // Method that fills the gaps in the benchmark data that can be used
    // throughout different components
    // --
    // This is not so performant but it's the only way to do this at the moment.
    // This will soon be refactored together with the entire components when
    // we will develop the new dashboard.
    fillGapsInBenchmarkData(data) {
        data = Object.entries(data);

        let index = 0;
        let previousDate = null;
        let newData = [];

        // Iterate through historical data
        for (let entry of data) {
            // Second to last row
            if (index > 0) {
                let addNumberOfDays = 1;

                // While the missing date is less than the next entry and less than today
                while (
                    new Date(entry[0]) > addDays(previousDate, addNumberOfDays) &&
                    addDays(previousDate, addNumberOfDays) < new Date()
                ) {
                    const dateMissing = addDays(previousDate, addNumberOfDays)
                        .toISOString()
                        .split('T')[0];

                    newData.push([
                        dateMissing,
                        newData[index - 1][1] // Previous price entry
                    ]);

                    // Iterate and add number of days
                    index++;
                    addNumberOfDays++;
                }

                // Push entry, set previous date and iterate
                previousDate = new Date(entry[0]);
                newData.push(entry);
                index++;

                continue;
            }

            // First row, set starting date from first entry
            previousDate = new Date(entry[0]);
            newData.push(entry);

            // Iterate
            index++;
        }

        if (newData.length > 0) {
            const lastDate = new Date(newData[newData.length - 1][0]);
            // Add future data?
            if (lastDate < new Date()) {
                let addNumberOfDays = 1;

                // While the missing date is less than or equal to today
                while (new Date() >= addDays(lastDate, addNumberOfDays)) {
                    // Get next date
                    const nextDate = addDays(lastDate, addNumberOfDays)
                        .toISOString()
                        .split('T')[0];

                    // Push entry
                    newData.push([
                        nextDate,
                        newData[index - 1][1] // Previous price entry
                    ]);

                    // Iterate
                    addNumberOfDays++;
                    index++;
                }
            }

            // Return array as object with { date.price } structure
            const newDataObject = newData.reduce((obj, [key, value]) => {
                obj[key] = value;
                return obj;
            }, {});
            return newDataObject;
        }

        // Return empty
        return {};
    }
}
